import { useState } from 'react';
import type { CookLog } from '@mlc/shared';

/**
 * Découpage de la fiche en trois onglets, sur petit écran seulement (US-40).
 *
 * Le geste le plus fréquent sur une fiche est l'aller-retour entre la liste
 * d'ingrédients et les étapes. En défilement continu il coûte plusieurs
 * balayages imprécis et fait perdre sa position ; en onglets il coûte un appui
 * sur une cible fixe et large.
 *
 * **Au-dessus de `lg`, les onglets disparaissent** et la fiche reprend sa mise
 * en page en deux colonnes : là, l'aller-retour n'existe pas, tout est visible
 * à la fois. Un onglet ne se justifie que par le manque de place.
 */
export const ONGLETS = ['apropos', 'ingredients', 'instructions'] as const;
export type Onglet = (typeof ONGLETS)[number];

const LIBELLES: Record<Onglet, string> = {
  apropos: 'À propos',
  ingredients: 'Ingrédients',
  instructions: 'Instructions',
};

/**
 * Onglet d'ouverture (US-40).
 *
 * **C'est une règle métier déguisée en détail d'interface**, et c'est elle qui
 * préserve le §4 : sans elle, les onglets ajouteraient un appui à la règle des
 * trois actions au lieu d'en retirer un.
 *
 * Une recette déjà cuisinée s'ouvre sur les instructions — on la connaît, on
 * vient la refaire, sa description n'a plus rien à apprendre. Une recette
 * jamais faite s'ouvre sur « À propos », où la photo et le texte servent encore
 * à décider.
 *
 * Les deux signaux sont acceptés, et c'est délibéré : `cook_logs` dit « je l'ai
 * faite et je l'ai noté », `cooked_before` dit « je l'ai faite avant que cette
 * application existe » (migration 008). Ignorer le second rendrait les
 * vingt-neuf recettes importées éternellement neuves.
 */
export function ongletDOuverture(input: {
  cook_logs: readonly Pick<CookLog, 'id'>[];
  cooked_before: number;
}): Onglet {
  return input.cook_logs.length > 0 || input.cooked_before === 1 ? 'instructions' : 'apropos';
}

export interface RecipeTabsProps {
  ouverture: Onglet;
  apropos: React.ReactNode;
  ingredients: React.ReactNode;
  instructions: React.ReactNode;
}

export function RecipeTabs({ ouverture, apropos, ingredients, instructions }: RecipeTabsProps): React.JSX.Element {
  const [actif, setActif] = useState<Onglet>(ouverture);
  const contenus: Record<Onglet, React.ReactNode> = { apropos, ingredients, instructions };

  return (
    <>
      {/* Sous `lg` : un onglet visible à la fois, la barre en bas, à hauteur du
          pouce. Au-dessus : tout est rendu, la barre disparaît, et c'est la
          grille à deux colonnes de la fiche qui reprend la main. */}
      <div className="flex flex-col gap-8 lg:contents">
        {ONGLETS.map((onglet) => (
          <div key={onglet} className={onglet === actif ? 'contents' : 'hidden lg:contents'}>
            {contenus[onglet]}
          </div>
        ))}
      </div>

      <nav
        aria-label="Sections de la recette"
        // `sticky bottom-0` plutôt que `fixed` : la barre reste dans le flux,
        // donc elle ne recouvre jamais le dernier paragraphe — le défaut relevé
        // par l'audit sur la barre de minuteurs, qui rendait la sortie du mode
        // cuisine inaccessible.
        className="sticky bottom-0 z-40 -mx-4 flex gap-1 border-t border-border bg-surface/95 px-4 py-2 backdrop-blur lg:hidden"
      >
        {ONGLETS.map((onglet) => (
          <button
            key={onglet}
            type="button"
            role="tab"
            aria-selected={onglet === actif}
            onClick={() => { setActif(onglet); }}
            // Plancher de 44 px du §4, étendu à toute la largeur disponible :
            // en cuisine, une cible large vaut mieux qu'une cible précise.
            className={`min-h-touch flex-1 rounded-control px-2 py-2 text-sm font-semibold transition-colors ${
              onglet === actif ? 'bg-primary text-primary-foreground' : 'text-foreground-secondary'
            }`}
          >
            {LIBELLES[onglet]}
          </button>
        ))}
      </nav>
    </>
  );
}
